/**
 * Utility functions for wildcard mask matching on part marks.
 */

/**
 * Tests if a mark string matches a wildcard mask pattern.
 * Supports:
 * - '*' matches 0 or more characters
 * - '?' matches 1 character
 * - Case-insensitive matching
 * - If no wildcard '*' or '?' is present, matches if mark contains the pattern as a substring (case-insensitive)
 *
 * Examples:
 * - '*AB*' -> matches 'ABC', 'CAB', '12AAB', 'AB'
 * - 'AB*'  -> matches 'ABC', 'AB', 'AB-01'
 * - '*AB'  -> matches 'CAB', '12AAB', 'AB'
 * - 'AB'   -> matches 'ABC', 'CAB', '12AAB', 'AB'
 */
export function matchesMarkMask(mark: string, mask: string): boolean {
  if (!mask || !mask.trim()) {
    return true;
  }

  const trimmed = mask.trim();

  // If pattern does not contain '*' or '?', treat as contains substring (case-insensitive)
  if (!trimmed.includes('*') && !trimmed.includes('?')) {
    return mark.toLowerCase().includes(trimmed.toLowerCase());
  }

  // Convert wildcard pattern to regular expression
  // Escape regex special characters except '*' and '?'
  const escaped = trimmed
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');

  const regex = new RegExp(`^${escaped}$`, 'i');
  return regex.test(mark);
}

/**
 * Filters a list of mark strings against a mask.
 * Returns the matching marks.
 */
export function filterCandidateMarks(availableMarks: string[], mask: string): string[] {
  if (!mask || !mask.trim()) {
    return availableMarks;
  }

  return availableMarks.filter((mark) => matchesMarkMask(mark, mask));
}
